import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { CustomEntity } from 'src/app/models/customEntity.model';
import { CustomField } from 'src/app/models/customField.model';
import { Sample } from 'src/app/models/sample.model';
import { CustomEntityService } from 'src/app/services/customEntity.service';
import { CustomObjectService } from 'src/app/services/customObject.service';
import { HttpHeaders } from '@angular/common/http';
import { EmployeeCustomFields } from 'src/app/enums/customFieldType.enum';
import { CUSTOM_ENTITY_TYPE_NAMES } from 'src/app/constants/customEntity.constants';
import { SampleTypeCategoryCombos, isVisibleOnly } from 'src/app/_helpers/sample-helper';
import { CustomObjectTypeEnum } from 'src/app/enums/customObjectType.enum';
import { buildEmployeeOptionConfig } from 'src/app/shared/form/employee-field-helpers';
import { FormField } from 'src/app/shared/form/formField.model';
import { CustomEntityDialogData, NodeOperationsComponent } from '../node-operations/node-operations.component';
import { MatDialog } from '@angular/material/dialog';
import { isCustomFieldKey } from 'src/app/shared/form/custom-field-helpers';
import { DEFAULT_PAGE_SIZE } from 'src/app/constants/customEntity.constants';

@Injectable()
export class SampleCustomEntityService {

  constructor(
    private customEntityService: CustomEntityService,
    private customObjectService: CustomObjectService,
    private dialog: MatDialog
  ) { }

  /**
   * Load custom entities (employees or locations) for the sample
   */
  loadCustomEntities(entity: Sample): Observable<{
    customEntities: CustomEntity[];
    customObjectsList: CustomField[];
    coId: number | string;
  }> {
    return this.customObjectService.getAll().pipe(
      switchMap(typeLookups => {
        const isLocationForm = isVisibleOnly([
          SampleTypeCategoryCombos.airArea,
          SampleTypeCategoryCombos.noiseArea,
          SampleTypeCategoryCombos.wipeArea,
          SampleTypeCategoryCombos.wipeBlank
        ])(entity);

        const entityType = typeLookups.find(t =>
          isLocationForm ? t.name.includes(CUSTOM_ENTITY_TYPE_NAMES.LOCATION) : t.name === CUSTOM_ENTITY_TYPE_NAMES.EMPLOYEE
        );

        if (!entityType) {
          return of({
            customEntities: [],
            customObjectsList: [],
            coId: 0
          });
        }

        const coId = entityType.id;
        let filterBy = {};
        if (entity?.endPoint) {
          filterBy = { name: entity.endPoint.name };
        }

        return this.customEntityService.get(
          '',
          'id',
          'asc',
          0,
          DEFAULT_PAGE_SIZE,
          JSON.stringify(filterBy),
          new HttpHeaders({ coid: coId })
        )
          .pipe(
            switchMap(res => {
              return this.customObjectService.getCustomObjectFields(coId).pipe(
                map(fields => {
                  const customObjectsList = fields.filter(f => isCustomFieldKey(f.property) && EmployeeCustomFields.includes(f.type));
                  return {
                    customEntities: res.data,
                    customObjectsList,
                    coId
                  };
                })
              );
            })
          );
      })
    );
  }

  /**
   * Search entity options for employee/location lookup
   */
  searchEntities(
    filter: string,
    coId: number,
    fields: FormField[]
  ): Observable<CustomEntity[]> {
    return this.customEntityService.get(
      filter,
      'id',
      'asc',
      0,
      DEFAULT_PAGE_SIZE,
      '',
      new HttpHeaders({ coid: coId })
    )
      .pipe(
        map(res => {
          const employeeField = fields?.find(f => f.property === 'search_employee');
          if (employeeField) {
            employeeField.options = res.data;
          }
          return res.data;
        })
      );
  }

  // Backward-compatible wrapper for older call sites.
  updateEntities(
    filter: string,
    coId: number,
    fields: FormField[]
  ): Observable<CustomEntity[]> {
    return this.searchEntities(filter, coId, fields);
  }

  /**
   * Handle custom entity selection (employee or location)
   */
  handleEntitySelection(
    event: { value: string },
    entity: Sample,
    fields: FormField[],
    customObjectsList: CustomField[],
    customEntities: CustomEntity[]
  ): void {
    if (!event?.value) {
      return;
    }

    const customEntity = customEntities.find(res => res.id === event?.value) as CustomEntity;
    if (!customEntity) {
      return;
    }

    entity.endPointId = customEntity.id;

    customObjectsList?.forEach(obj => {
      if (!obj.property || !isCustomFieldKey(obj.property)) {
        return;
      }
      entity[obj.property] = customEntity[obj.property];
    });

    if (customEntity.organizationLevelId) {
      entity.endpointOrganizationLevelId = customEntity.organizationLevelId;
      entity.organizationLevelId = customEntity.organizationLevelId;
    }
  }

  /**
   * Build employee search field configuration
   */
  buildEmployeeSearchConfig(customObjectsList: CustomField[]): {
    searchKeys: string[];
    optionTemplateFields: string[];
    optionTemplateLabelMap: Record<string, string>;
  } {
    const mappedKeys = customObjectsList
      .filter(obj => obj && typeof obj.property === 'string')
      .map(obj => obj.property);

    const config = buildEmployeeOptionConfig(customObjectsList);

    return {
      searchKeys: [...mappedKeys, 'name'],
      optionTemplateFields: config.optionTemplateFields,
      optionTemplateLabelMap: config.optionTemplateLabelMap
    };
  }

  /**
   * Open entity creation dialog (employee or location)
   */
  openEntityDialog(
    type: CustomObjectTypeEnum,
    coId: number | string,
    onSuccess: () => void
  ): void {
    const entityModel: CustomEntityDialogData = {
      id: '',
      label: type === CustomObjectTypeEnum.EmployeeEndPoint ? CUSTOM_ENTITY_TYPE_NAMES.EMPLOYEE : CUSTOM_ENTITY_TYPE_NAMES.LOCATION,
      customObjectId: String(coId),
      type: type,
    };

    const dialogRef = this.dialog.open(NodeOperationsComponent, {
      data: {
        ...entityModel,
        isReadonly: false
      },
      width: '400px',
      disableClose: true,
      panelClass: 'no-gps-and-building'
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        onSuccess?.();
      }
    });
  }
}
